<template>
  <div id="apt-news">
    <div class="page-header">
      <div class="title">부동산 월간 리포트</div>
      <div class="subtitle">매월 정리하는 부동산 시장 동향과 주요 이슈</div>
    </div>

    <div class="news-layout">
      <!-- 왼쪽: 타임라인 네비게이션 -->
      <aside class="timeline-nav">
        <div v-for="y in years" :key="y" class="year-group">
          <div class="year-label" @click="toggleYear(y)" :class="{ open: expandedYears[y] }">
            <span>{{ y }}년</span>
            <span class="arrow">{{ expandedYears[y] ? '&#9650;' : '&#9660;' }}</span>
          </div>
          <ul v-show="expandedYears[y]" class="month-list">
            <li
              v-for="m in getMonthsForYear(y)"
              :key="m"
              :class="{ active: selectedYear === y && selectedMonth === m }"
              @click="selectMonth(y, m)"
            >
              <span class="dot"></span>
              <span class="month-text">{{ m }}월</span>
              <span class="month-headline">{{ getHeadline(y, m) }}</span>
            </li>
          </ul>
        </div>
      </aside>

      <!-- 오른쪽: 상세 콘텐츠 -->
      <main class="news-content" v-if="currentNews">
        <div class="content-header">
          <div class="date-badge">{{ currentNews.year }}년 {{ currentNews.month }}월</div>
          <h2 class="headline">{{ currentNews.headline }}</h2>
          <p v-if="currentNews.summary" class="article-text" style="margin-top: 14px;">{{ currentNews.summary }}</p>
        </div>

        <article class="article-body">
          <section class="article-section">
            <h3><span class="section-icon">&#128220;</span>부동산 정책 동향</h3>
            <ul>
              <li v-for="(p, i) in currentNews.policy" :key="i">{{ p }}</li>
            </ul>
          </section>

          <section class="article-section">
            <h3><span class="section-icon">&#128240;</span>주요 이슈</h3>
            <ul>
              <li v-for="(s, i) in currentNews.issues" :key="i">{{ s }}</li>
            </ul>
          </section>

          <section class="article-section">
            <h3><span class="section-icon">&#127968;</span>서울 부동산 시장</h3>
            <div class="article-text">{{ currentNews.seoulMarket }}</div>
          </section>

          <section class="article-section">
            <h3><span class="section-icon">&#127974;</span>금리 동향</h3>
            <div class="article-text">{{ currentNews.interestRate }}</div>
          </section>

          <section v-if="currentNews.reconstruction" class="article-section">
            <h3><span class="section-icon">&#127959;</span>재건축·재개발 이슈</h3>
            <div class="article-text">{{ currentNews.reconstruction }}</div>
          </section>

          <section v-if="currentNews.outlook" class="article-section">
            <h3><span class="section-icon">&#128302;</span>이달의 전망 및 시사점</h3>
            <div class="article-text">{{ currentNews.outlook }}</div>
          </section>
        </article>

        <!-- 이전/다음 네비게이션 -->
        <div class="article-nav">
          <button class="nav-btn prev" :disabled="!prevNews" @click="goPrev">
            <template v-if="prevNews">
              <span class="nav-arrow">&larr;</span>
              <span class="nav-info">
                <span class="nav-date">{{ prevNews.year }}년 {{ prevNews.month }}월</span>
                <span class="nav-title">{{ prevNews.headline }}</span>
              </span>
            </template>
          </button>
          <button class="nav-btn next" :disabled="!nextNews" @click="goNext">
            <template v-if="nextNews">
              <span class="nav-info">
                <span class="nav-date">{{ nextNews.year }}년 {{ nextNews.month }}월</span>
                <span class="nav-title">{{ nextNews.headline }}</span>
              </span>
              <span class="nav-arrow">&rarr;</span>
            </template>
          </button>
        </div>
      </main>
    </div>

    <!-- 모바일: 월 선택 드롭다운 -->
    <div class="mobile-selector">
      <select v-model="mobileSelected" @change="onMobileSelect">
        <option
          v-for="item in sortedAllNews"
          :key="`${item.year}-${item.month}`"
          :value="`${item.year}-${item.month}`"
        >
          {{ item.year }}년 {{ item.month }}월 - {{ item.headline }}
        </option>
      </select>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import UtilMixin from "@/common/mixins/UtilMixin";
import newsDataImport, { type MonthlyNews } from "@/apt/assets/json/newsData";

export default defineComponent({
  name: "AptNewsView",
  mixins: [UtilMixin],
  data() {
    return {
      selectedYear: 2026 as number,
      selectedMonth: 1 as number,
      expandedYears: { 2025: true, 2026: true } as Record<number, boolean>,
      years: [2026, 2025] as number[],
      mobileSelected: "2026-1",
      newsData: newsDataImport as MonthlyNews[],
    };
  },
  computed: {
    sortedAllNews(): MonthlyNews[] {
      return [...this.newsData].sort((a, b) => {
        if (a.year !== b.year) return b.year - a.year;
        return b.month - a.month;
      });
    },
    currentIndex(): number {
      return this.sortedAllNews.findIndex(
        (n) => n.year === this.selectedYear && n.month === this.selectedMonth
      );
    },
    currentNews(): MonthlyNews | null {
      return this.currentIndex >= 0 ? this.sortedAllNews[this.currentIndex] : null;
    },
    prevNews(): MonthlyNews | null {
      return this.currentIndex < this.sortedAllNews.length - 1
        ? this.sortedAllNews[this.currentIndex + 1]
        : null;
    },
    nextNews(): MonthlyNews | null {
      return this.currentIndex > 0
        ? this.sortedAllNews[this.currentIndex - 1]
        : null;
    },
  },
  created() {
    // 가장 최신 월 선택
    const latest = this.sortedAllNews[0];
    if (latest) {
      this.selectedYear = latest.year;
      this.selectedMonth = latest.month;
      this.mobileSelected = `${latest.year}-${latest.month}`;
    }
  },
  methods: {
    getMonthsForYear(year: number): number[] {
      return this.newsData
        .filter((n) => n.year === year)
        .map((n) => n.month)
        .sort((a, b) => b - a);
    },
    getHeadline(year: number, month: number): string {
      const item = this.newsData.find((n) => n.year === year && n.month === month);
      return item ? item.headline : "";
    },
    selectMonth(year: number, month: number) {
      this.selectedYear = year;
      this.selectedMonth = month;
      this.mobileSelected = `${year}-${month}`;
      // 콘텐츠 영역 스크롤 상단
      const content = document.querySelector(".news-content");
      if (content) content.scrollTop = 0;
    },
    toggleYear(year: number) {
      this.expandedYears[year] = !this.expandedYears[year];
    },
    goPrev() {
      if (this.prevNews) {
        this.selectMonth(this.prevNews.year, this.prevNews.month);
      }
    },
    goNext() {
      if (this.nextNews) {
        this.selectMonth(this.nextNews.year, this.nextNews.month);
      }
    },
    onMobileSelect() {
      const [y, m] = this.mobileSelected.split("-").map(Number);
      this.selectMonth(y, m);
    },
  },
});
</script>
